// src/features/plan/policyClient.js
//
// MARKET-PLAN-POLICY-BAND: Thin fetch wrapper for GET /api/plan/policy.
// Returns { baseline, overrideCount } from the resolver endpoint.
//
// KEY SHAPES
// ──────────
// Raw API shape (compact keys, used by PolicyBand, StrategyTabPane,
// UnifiedContextStrip):
//   { rtoDays, deskRatio, hotelingPct, confGivebackPct, teamBlockWastePct,
//     sfFloor, hcBufferPct, ... }
//
// Normalised scenario shape (long keys, used by WhatIfPane / useWhatIfScenario):
//   { rtoDaysPerMonth, sharingRatio, hotelingHoldPct, confGivebackPct,
//     teamBlockWastePct, densityFloorSfPerSeat, targetBufferPct, ... }
//
// Use normaliseForScenario() to convert raw → scenario before passing to WhatIf
// components. Never pass raw compact-key objects to WhatIfPane.

const BASE = "/api/plan/policy";

async function fetchJson(url, options = {}) {
  const res = await fetch(url, {
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    ...options,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw Object.assign(new Error(body.error ?? `HTTP ${res.status}`), { status: res.status, body });
  }
  return res.json();
}

export function getPolicyBaseline() {
  return fetchJson(BASE);
}

/**
 * normaliseForScenario — convert a raw GET /api/plan/policy baseline (compact
 * keys) into the long-key shape that WhatIfPane / useWhatIfScenario expects.
 *
 * Raw → normalised key map:
 *   rtoDays    → rtoDaysPerMonth
 *   deskRatio  → sharingRatio
 *   hotelingPct → hotelingHoldPct
 *   sfFloor    → densityFloorSfPerSeat
 *   hcBufferPct → targetBufferPct
 *   confGivebackPct, teamBlockWastePct — passed through unchanged
 *
 * @param {object|null} raw  Raw baseline from the API (compact keys).
 * @returns {object|null}    Normalised baseline with long keys, or null if raw is null/undefined.
 */
export function normaliseForScenario(raw) {
  if (raw == null) return null;
  return {
    rtoDaysPerMonth:       raw.rtoDays,
    sharingRatio:          raw.deskRatio,
    hotelingHoldPct:       raw.hotelingPct,
    confGivebackPct:       raw.confGivebackPct,
    teamBlockWastePct:     raw.teamBlockWastePct,
    densityFloorSfPerSeat: raw.sfFloor,
    targetBufferPct:       raw.hcBufferPct,
  };
}
